<template>
  <div class="dashboard-page">
    <div v-if="!loading">
      <!-- ── Sales Overview (top, permission-gated) ────────────────────────── -->
      <section v-if="canViewStatistics" class="stats-section">
        <div class="section-header">
          <h2 class="section-title">Sales Overview</h2>
          <div class="flex items-center gap-3">
            <BaseSmallSpinner v-if="statsLoading" />
            <select
              v-model="statsSelectedDepartment"
              class="default-select bordered seller-select"
            >
              <option value="">All departments</option>
              <option
                v-for="dept in departments"
                :value="dept.value"
                :key="dept.value"
              >
                {{ dept.label }}
              </option>
            </select>
            <select
              v-model="statsSelectedSeller"
              class="default-select bordered seller-select"
            >
              <option value="">All sellers</option>
              <option
                v-for="seller in sellers"
                :value="seller.value"
                :key="seller.value"
              >
                {{ seller.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- KPI cards -->
        <div class="kpi-grid">
          <div
            v-for="card in kpiCards"
            :key="card.tab"
            class="kpi-card"
            :class="{ 'kpi-card--active': statsActiveTab === card.tab }"
            @click="statsActiveTab = card.tab"
          >
            <div class="kpi-card__label">{{ card.label }}</div>
            <div class="kpi-card__range">{{ card.dateRange }}</div>
            <div class="kpi-card__amount">
              {{ card.total | currencyFormatted(true) }}
            </div>
            <div class="kpi-card__orders">{{ card.orders }} orders</div>
            <div
              v-if="card.change !== null"
              class="kpi-card__change"
              :class="
                card.change >= 0
                  ? 'kpi-card__change--up'
                  : 'kpi-card__change--down'
              "
              :title="card.compRange"
            >
              {{ card.change >= 0 ? "▲" : "▼" }}
              {{ Math.abs(card.change) }}% vs {{ card.prevLabel }}
            </div>
            <div
              v-else-if="card.compRange === null"
              class="kpi-card__change kpi-card__change--neutral"
            >
              —
            </div>
            <div v-else class="kpi-card__change kpi-card__change--neutral">
              — no prev. data
            </div>
            <div v-if="card.prediction" class="kpi-card__prediction">
              On pace: {{ card.prediction | currencyFormatted(true) }}
            </div>
          </div>
        </div>

        <!-- Chart -->
        <div class="chart-card">
          <div class="chart-tabs">
            <button
              v-for="card in kpiCards"
              :key="card.tab"
              class="chart-tab"
              :class="{ 'chart-tab--active': statsActiveTab === card.tab }"
              @click="statsActiveTab = card.tab"
            >
              {{ card.label }}
            </button>
            <div class="chart-tab-datepicker">
              <input
                type="date"
                class="regular bordered"
                :max="today"
                v-model="statsCustomDate"
                title="Filter a specific day"
              />
            </div>
          </div>
          <div
            class="chart-body"
            v-if="
              !statsLoading &&
                !statsCustomLoading &&
                statsChartLabels &&
                statsChartLabels.length
            "
          >
            <BarChart
              :key="statsChartKey"
              :labels="statsChartLabels"
              :chartData="statsChartDatasets"
              :extraOptions="statsChartOptions"
            />
          </div>
          <div
            v-else-if="statsLoading || statsCustomLoading"
            class="chart-body chart-body--empty"
          >
            <BaseSmallSpinner />
          </div>
          <div v-else class="chart-body chart-body--empty text-gray-400">
            No data for this period
          </div>
        </div>
      </section>

      <!-- ── Orders Status ───────────────────────────────────────────────────── -->
      <section
        v-if="ordersStatus.length > 0"
        class="dashboard-card orders-status-section"
      >
        <h2 class="section-title">
          {{ $t("message.dashboard_page.orders_status") }}
        </h2>
        <div class="orders-status-grid">
          <div
            v-for="status in ordersStatus"
            :key="status.key"
            class="order-status-chip"
            :class="{
              'order-status-chip--alert':
                (status.key === 'VIN verification' ||
                  status.key === 'Dropship Pending' ||
                  status.key === 'Local Dropship') &&
                status.display > 0
            }"
          >
            <span class="order-status-chip__count">{{ status.display }}</span>
            <span class="order-status-chip__label">{{
              getTranslatedStatus(status.key)
            }}</span>
          </div>
        </div>
      </section>

      <!-- ── USA-specific tools ─────────────────────────────────────────────── -->
      <template v-if="activeStore && activeStore.code === 'nkr_usa'">
        <div class="usa-tools-grid">
          <!-- Price Group Calculator -->
          <section class="dashboard-card">
            <h3 class="section-title">
              {{ $t("message.dashboard_page.price_group_calculator") }}
            </h3>
            <PriceGroupCalculator :priceGroups="priceGroups" />
          </section>

          <!-- Shipping Calculator -->
          <section class="dashboard-card">
            <ShippingCalculator v-model="showCalculator" />
            <button
              class="btn-info-button flex flex-row items-center gap-2 mt-3"
              @click="showCalculator = true"
            >
              {{ $t("message.dashboard_page.shipping_calculator") }}
              <span class="w-5 h-5">
                <img
                  class="w-full h-full"
                  src="@/assets/icons/calculator.svg"
                />
              </span>
            </button>
          </section>
        </div>

        <!-- Important Links -->
        <section class="dashboard-card">
          <h2 class="section-title">
            {{ $t("message.dashboard_page.important_links") }}
          </h2>
          <div class="links-grid">
            <a
              href="https://www.dropbox.com/scl/fi/6q0j4ccwr534axxfm3poq/casos-devuletos_-por-devolver-amazon.xlsx?dl=0&rlkey=yz96p5ospg0opumlp5jynpzfp"
              target="_blank"
              class="link-chip"
              >{{ $t("message.dashboard_page.returns_amazon") }}</a
            >
            <a
              href="https://www.dropbox.com/scl/fi/g7zho7r6pfch79nz08tvs/Casos-devueltos_-por-devolver-ebay.xlsx?dl=0&rlkey=135pwtg6kq09zvbh2kzxq4hbh"
              target="_blank"
              class="link-chip"
              >{{ $t("message.dashboard_page.returns_ebay") }}</a
            >
            <a
              href="https://www.dropbox.com/scl/fi/x92fbz6ox1iq00qk3bfow/Returns-Website.xlsx?dl=0&rlkey=zdua7znpv934gnb928nspr8o5"
              target="_blank"
              class="link-chip"
              >{{ $t("message.dashboard_page.returns_website") }}</a
            >
            <a
              href="https://www.dropbox.com/scl/fi/g99ahnq94167wxzpeeucy/claims-UPS.xlsx?dl=0&rlkey=oubfag6izyfca4g1wxtac3i56"
              target="_blank"
              class="link-chip"
              >{{ $t("message.dashboard_page.claims_ups") }}</a
            >
            <a
              href="https://www.dropbox.com/scl/fi/346my7rf26r2u2niyqet8/Claims-USPS.xlsx?dl=0&rlkey=e628i0vdetemt3y1o4s5zvt0m"
              target="_blank"
              class="link-chip"
              >{{ $t("message.dashboard_page.claims_usps") }}</a
            >
            <a
              :href="`${storeUrl}/warehouse`"
              target="_blank"
              class="link-chip"
            >
              {{ $t("message.dashboard_page.warehouse_labels") }}
            </a>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSeadf3DnsJrIeaSxYEY-lg5lc2QZyTi_IBvAk3pBMY0I6VS0Q/viewform?usp=header"
              target="_blank"
              class="link-chip link-chip--highlight"
            >
              {{ $t("message.dashboard_page.new_customers_form") }}
              <svg-icon
                type="mdi"
                :path="mdiAccount"
                class="inline w-4 h-4 ml-1"
              />
            </a>
          </div>
        </section>
      </template>
    </div>
    <div v-else class="flex justify-center items-center h-40 text-gray-400">
      {{ $t("message.dashboard_page.loading") }}
    </div>
  </div>
</template>

<script>
import moment from "moment";
import PriceGroupCalculator from "@/components/product/PriceGroupCalculator.vue";
import ShippingCalculator from "../../components/order/ShippingCalculator.vue";
import BarChart from "@/components/BarChart.vue";
import {
  getOrdersQtyByStatus,
  loadStatistics
} from "@/api/store/OrdersService.js";
import { getSelectedStoreURL } from "@/api/httpStore.js";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiAccount } from "@mdi/js";

const fmt = "YYYY-MM-DD";
const fmtDisp = "MMM D";
const today = moment().format(fmt);
const yesterday = moment()
  .subtract(1, "days")
  .format(fmt);
const startOfWeek = moment()
  .startOf("isoWeek")
  .format(fmt);
const startOfLastWeek = moment()
  .subtract(1, "week")
  .startOf("isoWeek")
  .format(fmt);
// Fair: same weekday last week instead of full last week
const endOfLastWeekFair = moment()
  .subtract(1, "week")
  .format(fmt);
const startOfMonth = moment()
  .startOf("month")
  .format(fmt);
const startOfLastMonth = moment()
  .subtract(1, "month")
  .startOf("month")
  .format(fmt);
const endOfLastMonth = moment()
  .subtract(1, "month")
  .endOf("month")
  .format(fmt);
// Fair: same day-of-month in last month (capped at last month's length)
const endOfLastMonthFair = moment()
  .subtract(1, "month")
  .date(
    Math.min(
      moment().date(),
      moment()
        .subtract(1, "month")
        .daysInMonth()
    )
  )
  .format(fmt);
const startOfYear = moment()
  .startOf("year")
  .format(fmt);
const startOfLastYear = moment()
  .subtract(1, "year")
  .startOf("year")
  .format(fmt);
// Fair: same date last year
const endOfLastYearFair = moment()
  .subtract(1, "year")
  .format(fmt);

export default {
  name: "dashboardPage",
  components: { PriceGroupCalculator, ShippingCalculator, BarChart, SvgIcon },
  data() {
    return {
      publicPath: process.env.VUE_APP_IMG_BASE_URL,
      loading: true,
      showCalculator: false,
      ordersStatus: [],
      mdiAccount,
      // stats
      statsSelectedSeller: "",
      statsSelectedDepartment: "",
      statsActiveTab: "month",
      statsCustomDate: "",
      statsLoading: false,
      statsCustomLoading: false,
      statsChartKey: 0,
      statsPeriods: {
        today: {
          summary: null,
          labels: [],
          productsData: [],
          totalData: [],
          shippingData: []
        },
        yesterday: { summary: null },
        week: {
          summary: null,
          labels: [],
          productsData: [],
          totalData: [],
          shippingData: []
        },
        lastWeek: { summary: null },
        month: {
          summary: null,
          labels: [],
          productsData: [],
          totalData: [],
          shippingData: []
        },
        lastMonth: {
          summary: null,
          labels: [],
          productsData: [],
          totalData: [],
          shippingData: []
        },
        year: {
          summary: null,
          labels: [],
          productsData: [],
          totalData: [],
          shippingData: []
        },
        lastYear: { summary: null },
        customDay: {
          summary: null,
          labels: [],
          productsData: [],
          totalData: [],
          shippingData: []
        },
        customDayPrev: { summary: null }
      },
      today
    };
  },
  computed: {
    storeUrl() {
      return getSelectedStoreURL();
    },
    activeStore() {
      return this.$store.getters["general/getActiveStore"];
    },
    priceGroups() {
      return this.$store.getters["general/getPriceGroups"];
    },
    canViewStatistics() {
      return this.$store.getters["auth/userHasPermission"](
        "can_view_statistics"
      );
    },
    sellers() {
      return this.$store.getters["general/getSellers"].map(s => ({
        label: s.name,
        value: s.email
      }));
    },
    departments() {
      return this.$store.getters["general/getDepartments"].map(d => ({
        label: d.name,
        value: d.full.departamentoId
      }));
    },
    kpiCards() {
      const cards = [
        {
          tab: "today",
          label: "Today",
          prevLabel: "yesterday",
          dateRange: moment(today).format("ddd, MMM D"),
          compRange: `vs ${moment(yesterday).format("MMM D")}`,
          total: this.statsPeriods.today.summary?.totalProductsAmount ?? 0,
          orders: this.statsPeriods.today.summary?.totalOrdersQty ?? 0,
          change: this.calcChange(
            this.statsPeriods.today.summary,
            this.statsPeriods.yesterday.summary
          ),
          prediction: null
        },
        {
          tab: "week",
          label: "This Week",
          prevLabel: "last week",
          dateRange: `${moment(startOfWeek).format(fmtDisp)} – ${moment(
            today
          ).format(fmtDisp)}`,
          compRange: `vs ${moment(startOfLastWeek).format(fmtDisp)} – ${moment(
            endOfLastWeekFair
          ).format(fmtDisp)}`,
          total: this.statsPeriods.week.summary?.totalProductsAmount ?? 0,
          orders: this.statsPeriods.week.summary?.totalOrdersQty ?? 0,
          change: this.calcChange(
            this.statsPeriods.week.summary,
            this.statsPeriods.lastWeek.summary
          ),
          prediction: this.calcPrediction(
            this.statsPeriods.week.summary,
            moment().isoWeekday(),
            7
          )
        },
        {
          tab: "month",
          label: "This Month",
          prevLabel: "last month",
          dateRange: `${moment(startOfMonth).format(fmtDisp)} – ${moment(
            today
          ).format(fmtDisp)}`,
          compRange: `vs ${moment(startOfLastMonth).format(fmtDisp)} – ${moment(
            endOfLastMonthFair
          ).format(fmtDisp)}`,
          total: this.statsPeriods.month.summary?.totalProductsAmount ?? 0,
          orders: this.statsPeriods.month.summary?.totalOrdersQty ?? 0,
          change: this.calcChange(
            this.statsPeriods.month.summary,
            this.statsPeriods.lastMonth.summary
          ),
          prediction: this.calcPrediction(
            this.statsPeriods.month.summary,
            moment().date(),
            moment().daysInMonth()
          )
        },
        {
          tab: "lastMonth",
          label: "Last Month",
          prevLabel: null,
          dateRange: `${moment(startOfLastMonth).format(fmtDisp)} – ${moment(
            endOfLastMonth
          ).format(fmtDisp)}`,
          compRange: null,
          total: this.statsPeriods.lastMonth.summary?.totalProductsAmount ?? 0,
          orders: this.statsPeriods.lastMonth.summary?.totalOrdersQty ?? 0,
          change: null,
          prediction: null
        },
        {
          tab: "year",
          label: "This Year",
          prevLabel: "last year",
          dateRange: `${moment(startOfYear).format(fmtDisp)} – ${moment(
            today
          ).format(fmtDisp)}`,
          compRange: `vs ${moment(startOfLastYear).format(fmtDisp)} – ${moment(
            endOfLastYearFair
          ).format("MMM D, YYYY")}`,
          total: this.statsPeriods.year.summary?.totalProductsAmount ?? 0,
          orders: this.statsPeriods.year.summary?.totalOrdersQty ?? 0,
          change: this.calcChange(
            this.statsPeriods.year.summary,
            this.statsPeriods.lastYear.summary
          ),
          prediction: this.calcPrediction(
            this.statsPeriods.year.summary,
            moment().dayOfYear(),
            moment().isLeapYear() ? 366 : 365
          )
        }
      ];
      if (this.statsCustomDate) {
        const prevDay = moment(this.statsCustomDate).subtract(7, "days");
        cards.push({
          tab: "customDay",
          label: moment(this.statsCustomDate).format("MMM D"),
          prevLabel: "prev. week",
          dateRange: moment(this.statsCustomDate).format("ddd, MMM D, YYYY"),
          compRange: `vs ${prevDay.format("ddd, MMM D")}`,
          total: this.statsPeriods.customDay.summary?.totalProductsAmount ?? 0,
          orders: this.statsPeriods.customDay.summary?.totalOrdersQty ?? 0,
          change: this.calcChange(
            this.statsPeriods.customDay.summary,
            this.statsPeriods.customDayPrev.summary
          ),
          prediction: null
        });
      }
      return cards;
    },
    statsChartOptions() {
      return {
        scales: {
          yAxes: [
            {
              id: "left",
              position: "left",
              ticks: { beginAtZero: true },
              gridLines: { display: true }
            },
            {
              id: "right",
              position: "right",
              ticks: { beginAtZero: true },
              gridLines: { display: false }
            }
          ],
          xAxes: [{ gridLines: { display: false } }]
        }
      };
    },
    statsChartLabels() {
      return this.statsPeriods[this.statsActiveTab].labels.map(l =>
        this.formatChartLabel(l)
      );
    },
    statsChartDatasets() {
      const p = this.statsPeriods[this.statsActiveTab];
      return [
        {
          label: "Products",
          backgroundColor: "rgba(99,102,241,0.45)",
          borderColor: "rgba(99,102,241,0.8)",
          pointBackgroundColor: "rgba(99,102,241,1)",
          pointBorderColor: "#fff",
          data: p.productsData
        },
        {
          label: "Order Total",
          backgroundColor: "rgba(34,197,94,0.35)",
          borderColor: "rgba(34,197,94,0.7)",
          pointBorderColor: "#fff",
          data: p.totalData
        },
        {
          label: "Shipping",
          backgroundColor: "rgba(251,146,60,0.55)",
          borderColor: "rgba(251,146,60,0.9)",
          pointBorderColor: "#fff",
          yAxisID: "right",
          data: p.shippingData
        }
      ];
    },
    statusKeyMap() {
      return {
        "Awaiting Payment": "awaiting_payment",
        "Awaiting Shipment": "awaiting_shipment",
        "Cancel request pending": "cancel_request_pending",
        Draft: "draft",
        Processing: "processing",
        "On Hold": "on_hold",
        "Dropship processed": "dropship_processed",
        "Dropship Pending": "dropship_pending",
        "Local Dropship": "local_dropship",
        "VIN verification": "vin_verification",
        warehouse_allocation: "warehouse_allocation",
        "new Returns": "new_returns",
        "open Returns": "open_returns"
      };
    }
  },
  watch: {
    statsSelectedSeller() {
      this.loadStats();
    },
    statsSelectedDepartment() {
      this.loadStats();
      if (this.statsCustomDate) this.loadCustomDay(this.statsCustomDate);
    },
    statsActiveTab() {
      this.statsChartKey++;
    },
    statsCustomDate(date) {
      if (date) {
        this.statsActiveTab = "customDay";
        this.loadCustomDay(date);
      }
    }
  },
  mounted() {
    this.loading = false;
    this.getOrdersStatus();
    if (this.canViewStatistics) this.loadStats();
  },
  methods: {
    getTranslatedStatus(statusKey) {
      const key = this.statusKeyMap[statusKey];
      return key
        ? this.$t(`message.dashboard_page.order_statuses.${key}`)
        : statusKey;
    },
    getOrdersStatus() {
      getOrdersQtyByStatus().then(res => {
        for (const status in res) {
          this.ordersStatus.push({ key: status, display: res[status] });
        }
      });
    },
    fetchPeriod(fromDate, toDate, groupBy = "day") {
      return loadStatistics({
        orderType: { web: true, seller: true },
        includeCreatedByUser: true,
        includeCreatedBySeller: true,
        includeCredit: true,
        groupOrdersby: groupBy,
        seller: this.statsSelectedSeller,
        departamentoId: this.statsSelectedDepartment || undefined,
        fromDate,
        toDate
      });
    },
    async loadStats() {
      this.statsLoading = true;
      try {
        const [
          todayRes,
          yesterdayRes,
          weekRes,
          lastWeekRes,
          monthRes,
          lastMonthRes,
          yearRes,
          lastYearRes
        ] = await Promise.all([
          this.fetchPeriod(today, today),
          this.fetchPeriod(yesterday, yesterday),
          this.fetchPeriod(startOfWeek, today),
          this.fetchPeriod(startOfLastWeek, endOfLastWeekFair),
          this.fetchPeriod(startOfMonth, today),
          this.fetchPeriod(startOfLastMonth, endOfLastMonth),
          this.fetchPeriod(startOfYear, today, "month"),
          this.fetchPeriod(startOfLastYear, endOfLastYearFair, "month")
        ]);

        const map = r => ({
          summary: r.summary,
          labels: r.dates,
          productsData: r.productsTotal,
          totalData: r.total,
          shippingData: r.shippingTotal
        });

        Object.assign(this.statsPeriods.today, map(todayRes));
        this.statsPeriods.yesterday.summary = yesterdayRes.summary;
        Object.assign(this.statsPeriods.week, map(weekRes));
        this.statsPeriods.lastWeek.summary = lastWeekRes.summary;
        Object.assign(this.statsPeriods.month, map(monthRes));
        Object.assign(this.statsPeriods.lastMonth, map(lastMonthRes));
        Object.assign(this.statsPeriods.year, map(yearRes));
        this.statsPeriods.lastYear.summary = lastYearRes.summary;

        this.statsChartKey++;
      } finally {
        this.statsLoading = false;
      }
    },
    async loadCustomDay(date) {
      this.statsCustomLoading = true;
      const prevWeekDay = moment(date)
        .subtract(7, "days")
        .format("YYYY-MM-DD");
      try {
        const [dayRes, prevRes] = await Promise.all([
          this.fetchPeriod(date, date),
          this.fetchPeriod(prevWeekDay, prevWeekDay)
        ]);
        Object.assign(this.statsPeriods.customDay, {
          summary: dayRes.summary,
          labels: dayRes.dates,
          productsData: dayRes.productsTotal,
          totalData: dayRes.total,
          shippingData: dayRes.shippingTotal
        });
        this.statsPeriods.customDayPrev.summary = prevRes.summary;
        this.statsChartKey++;
      } finally {
        this.statsCustomLoading = false;
      }
    },
    calcChange(current, previous) {
      if (!current || !previous) return null;
      const curr = parseFloat(current.totalProductsAmount);
      const prev = parseFloat(previous.totalProductsAmount);
      if (prev === 0) return curr > 0 ? 100 : 0;
      return Math.round(((curr - prev) / prev) * 100);
    },
    // Backend returns labels like "Monday - 7 - April - 2026" (day) or "April - 2026" (month).
    // Shorten them for the chart X-axis.
    formatChartLabel(label) {
      if (!label) return label;
      const parts = label.split(" - ");
      if (parts.length === 4) {
        // day view: "Weekday - D - Month - YYYY" → "Apr 7"
        const [, day, month, year] = parts;
        const d = moment(`${month} ${day} ${year}`, "MMMM D YYYY");
        return d.isValid() ? d.format("MMM D") : label;
      }
      if (parts.length === 2) {
        // month view: "Month - YYYY" → "Apr '26"
        const [month, year] = parts;
        const d = moment(`${month} ${year}`, "MMMM YYYY");
        return d.isValid() ? d.format("MMM 'YY") : label;
      }
      return label;
    },
    calcPrediction(summary, elapsed, total) {
      if (!summary || elapsed <= 0 || elapsed >= total) return null;
      const curr = parseFloat(summary.totalProductsAmount);
      if (!curr) return null;
      return ((curr / elapsed) * total).toFixed(2);
    }
  }
};
</script>

<style lang="scss">
.dashboard-page {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* ── Shared card shell ──────────────────────────────────── */
.dashboard-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

/* ── Section header ─────────────────────────────────────── */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.75rem;
}
.seller-select {
  min-width: 160px;
}

/* ── Stats section ──────────────────────────────────────── */
.stats-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

/* ── KPI grid ───────────────────────────────────────────── */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.875rem;
  margin-bottom: 1.25rem;
}

.kpi-card {
  border: 1.5px solid #e5e7eb;
  border-radius: 0.625rem;
  padding: 1rem 1.125rem;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  &:hover {
    border-color: #a5b4fc;
    box-shadow: 0 2px 6px rgba(99, 102, 241, 0.1);
  }
  &--active {
    border-color: #6366f1;
    background: linear-gradient(135deg, #f5f3ff 0%, #eef2ff 100%);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  }
  &__label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
    margin-bottom: 0.1rem;
  }
  &__range {
    font-size: 0.72rem;
    color: #9ca3af;
    margin-bottom: 0.35rem;
  }
  &__amount {
    font-size: 1.6rem;
    font-weight: 800;
    color: #111827;
    line-height: 1.1;
    margin-bottom: 0.2rem;
  }
  &__orders {
    font-size: 0.8rem;
    color: #9ca3af;
    margin-bottom: 0.35rem;
  }
  &__change {
    font-size: 0.8rem;
    font-weight: 600;
    cursor: default;
    &--up {
      color: #16a34a;
    }
    &--down {
      color: #dc2626;
    }
    &--neutral {
      color: #9ca3af;
      font-weight: 400;
    }
  }
  &__prediction {
    margin-top: 0.4rem;
    font-size: 0.75rem;
    color: #6366f1;
    font-weight: 600;
    border-top: 1px dashed #e0e7ff;
    padding-top: 0.35rem;
  }
}

/* ── Chart card ─────────────────────────────────────────── */
.chart-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  overflow: hidden;
}
.chart-tabs {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}
.chart-tab-datepicker {
  margin-left: auto;
  padding: 0.25rem 0.75rem;
  display: flex;
  align-items: center;
  input[type="date"] {
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
    border-radius: 0.375rem;
    border: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    cursor: pointer;
    &:focus {
      outline: none;
      border-color: #6366f1;
    }
  }
}
.chart-tab {
  padding: 0.5rem 1.25rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #6b7280;
  border: none;
  background: transparent;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.12s, border-color 0.12s;
  &:hover {
    color: #4f46e5;
  }
  &--active {
    color: #4f46e5;
    border-bottom-color: #4f46e5;
    background: #fff;
  }
}
.chart-body {
  padding: 1rem 1rem 0.5rem;
  height: 280px;
  position: relative;
  &--empty {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* ── Orders status ──────────────────────────────────────── */
.orders-status-section {
}
.orders-status-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.order-status-chip {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 0.3rem 0.85rem;
  font-size: 1.5rem;
  &__count {
    font-weight: 800;
    color: #111827;
  }
  &__label {
    color: #4b5563;
  }
  &--alert {
    background: #fef2f2;
    .order-status-chip__count {
      color: #dc2626;
    }
    .order-status-chip__label {
      color: #b91c1c;
      font-weight: 600;
    }
  }
}

/* ── USA tools grid ─────────────────────────────────────── */
.usa-tools-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

/* ── Links grid ─────────────────────────────────────────── */
.links-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.link-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.9rem;
  background: #f3f4f6;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #4b5563;
  text-decoration: none;
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: #e0e7ff;
    color: #4f46e5;
  }
  &--highlight {
    background: #fef2f2;
    color: #dc2626;
    &:hover {
      background: #fee2e2;
    }
  }
}
</style>
